import { Button, Form, Input, message, Radio, Select, Tabs } from 'antd';
import StoreSelector from 'components/StoreSelector';
import { MAX_SELECTED_LENGTH } from 'components/StoreSelector/hooks/useStoreSelector';
import { StoreSelectorValue } from 'components/StoreSelector/interface';
import dayjs, { Dayjs } from 'lib/dayjs';
import { useEffect, useState, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { StoreInfo, searchStoreList } from 'services/api/store';
import { officialTypeId, useStatusLabel, useTypeLabel } from '../hooks/useLabel';
import styled from '@emotion/styled';
import OperatorSelect from './OperatorSelect';
import CreatorSelect from './CreatorSelect';
import { ClearOutlined } from '@ant-design/icons';
import { getDefaultTimeBegin, getDefaultTimeEnd } from '../hooks/useFilterQuery';
import TaskSelect from './TaskSelect';
import { TaskProvider, useTaskContext } from './context';
import { DatePicker } from 'components/DatePicker';
import { orderBy } from 'lodash-es';
import { css } from '@emotion/css';
import { todoSearchFilterCache } from 'services/cache';

export interface FilterValue extends Util.PartialPartial<Todo.Filter, 'type'> {
  follower?: StoreSelectorValue['follower'];
}

interface FilterProps<T> {
  filter: T;
  onFilterChange: (filter: T) => void;
}

type TodoFilterProps<K extends keyof FilterValue = keyof FilterValue> = FilterProps<
  Pick<FilterValue, K>
>;

export function HeaderFilter({
  filter,
  onFilterChange,
}: FilterProps<Omit<FilterValue, 'type' | 'todoStatus' | 'pageNum' | 'pageSize'>>) {
  const { t } = useTranslate();

  const [key, setKey] = useState(0);

  const onChange = (value: Partial<FilterValue>) => {
    onFilterChange({
      ...filter,
      ...value,
    });
  };

  const TaskField = (
    <Form.Item label={t('marketing.任务名称')}>
      <TaskSelect value={filter.taskId} onChange={(taskId) => onChange({ taskId })} />
    </Form.Item>
  );

  const storeField = (
    <Form.Item label={t('marketing.店铺')}>
      <StoreFilter filter={filter} onFilterChange={onChange} />
    </Form.Item>
  );

  const [sb, setSb] = useState('a');

  const multiField = (
    <Form.Item>
      <Input.Group compact>
        <Select onChange={(e) => setSb(e)} value={sb}>
          <Select.Option value="a">{t('todo.受理客服')}</Select.Option>
          <Select.Option value="b">{t('todo_creator')}</Select.Option>
        </Select>
        {sb === 'a' ? (
          <OperatorSelect
            value={filter.operators?.map((_) => Number(_))}
            onChange={(operators) => onChange({ operators })}
          />
        ) : (
          <CreatorSelect
            value={filter.createOperators?.map((_) => Number(_))}
            onChange={(createOperators) => onChange({ createOperators })}
          />
        )}
      </Input.Group>
    </Form.Item>
  );

  const timeField = (
    <Form.Item label={t('创建待办时间')}>
      <TimeFilter filter={filter} onFilterChange={onChange} />
    </Form.Item>
  );

  const hasValue = filterHasValue(filter);

  const onClear = () => {
    onChange({
      platform: undefined,
      region: undefined,
      follower: '0',
      storeIds: [],
      operators: [],
      timeBegin: getDefaultTimeBegin(),
      timeEnd: getDefaultTimeEnd(),
      relatedTradeId: undefined,
      relatedReturnId: undefined,
      buyerNick: undefined,
      todoId: undefined,
      description: undefined,
    });

    setKey((key) => key + 1);
  };

  return (
    <FilterForm layout="inline">
      <div className="flex items-center">
        <TaskProvider>
          {TaskField}
          {storeField}
        </TaskProvider>
        {multiField}
        {timeField}
      </div>
      <div className="flex items-center mt-4">
        <KeywordsFilter key={key} filter={filter} onFilterChange={onChange} />
        {hasValue && (
          <Button type="link" icon={<ClearOutlined />} onClick={onClear}>
            {t('todo.清除条件')}
          </Button>
        )}
      </div>
    </FilterForm>
  );
}

export function filterHasValue(filter: Partial<FilterValue>) {
  const {
    platform,
    region,
    follower,
    storeIds,
    operators,
    timeBegin,
    timeEnd,
    relatedTradeId,
    relatedReturnId,
    buyerNick,
    todoId,
    description,
  } = filter;

  return (
    platform ||
    region ||
    follower === '1' ||
    storeIds?.length ||
    operators?.length ||
    timeBegin !== getDefaultTimeBegin() ||
    timeEnd !== getDefaultTimeEnd() ||
    relatedTradeId ||
    relatedReturnId ||
    buyerNick ||
    todoId ||
    description
  );
}

function StoreFilter({
  filter,
  onFilterChange,
}: TodoFilterProps<'platform' | 'region' | 'follower' | 'storeIds'>) {
  const { platform, region, follower, storeIds } = filter;

  const hasValue = Boolean(
    platform || region || follower === '1' || (storeIds && storeIds.length > 0),
  );

  const { t } = useTranslate();

  const [storeList, setStoreList] = useState<StoreInfo[] | undefined>();

  const [loading, setLoading] = useState(false);

  const refsCurrent = { storeList };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { storeList },
    } = refs;

    if ((!storeIds || storeIds.length === 0) && storeList && storeList.length) {
      setStoreList([]);

      return;
    }

    if ((storeList && storeList.length) || !(storeIds && storeIds.length)) {
      return;
    }

    setLoading(true);

    searchStoreList({
      storeIds: storeIds.map((id) => parseInt(id, 10)),
      cursor: 0,
      page_size: MAX_SELECTED_LENGTH,
    })
      .then((res) => {
        setStoreList(res.data.data.data);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [storeIds]);

  const { value: whiteStoreList } = useTaskContext();

  return (
    <StoreSelector
      scene="VISIT_TODO_MANAGEMENT_CENTER"
      displayUnreadCount={false}
      displayTriggerIcon={false}
      whiteStoreList={whiteStoreList}
      value={{
        platform,
        region,
        follower,
        storeList,
      }}
      onChange={(value) => {
        const { platform, region, follower, storeList } = value;

        setStoreList(storeList);

        if (platform === 'WA_BIZ') {
          return;
        }

        onFilterChange({
          platform,
          region,
          follower,
          storeIds: storeList ? storeList.map(({ storeId }) => storeId) : undefined,
        });
      }}
      displayNodeRender={(displayNode) => {
        return (
          <Select
            className="storeSelect"
            mode="multiple"
            showArrow
            loading={loading}
            placeholder={t('remind.选择店铺')}
            value={hasValue ? ['value'] : []}
            tagRender={() => <>{displayNode}</>}
            open={false}
          />
        );
      }}
    />
  );
}

function TimeFilter({ filter, onFilterChange }: TodoFilterProps<'timeBegin' | 'timeEnd'>) {
  const { timeBegin, timeEnd } = filter;

  const getValue = (): [Dayjs | null, Dayjs | null] | null =>
    timeBegin && timeEnd ? [dayjs(Number(timeBegin)), dayjs(Number(timeEnd))] : null;

  const [visible, setVisible] = useState(false);

  const [value, onChange] = useState(getValue);

  const [start, end] = value || [];

  return (
    <DatePicker.RangePicker
      allowClear={false}
      value={visible ? value : getValue()}
      onCalendarChange={onChange}
      onChange={(value) => {
        const [start, end] = value || [];

        if (start && end) {
          onFilterChange({
            timeBegin: start.startOf('days').valueOf().toString(),
            timeEnd: end.endOf('days').valueOf().toString(),
          });
        }
      }}
      onOpenChange={(visible) => {
        setVisible(visible);
        onChange(null);
      }}
      disabledDate={(date) =>
        date.isAfter(dayjs()) ||
        Boolean(start && date.isAfter(start.clone().add(30, 'days').endOf('days'))) ||
        Boolean(end && date.isBefore(end.clone().subtract(30, 'days').startOf('days')))
      }
    />
  );
}

function KeywordsFilter({
  onFilterChange,
}: TodoFilterProps<'relatedTradeId' | 'relatedReturnId' | 'buyerNick' | 'todoId' | 'description'>) {
  const { t } = useTranslate();
  const [selectedOption, setSelectedOption] = useState<string>(
    todoSearchFilterCache.get<string>() || 'relatedTradeId',
  );

  const searchOptions = [
    {
      label: t('订单编号'),
      value: 'relatedTradeId',
    },
    {
      label: t('退单编号'),
      value: 'relatedReturnId',
    },
    {
      label: t('买家昵称'),
      value: 'buyerNick',
    },
    {
      label: t('todo.待办编号'),
      value: 'todoId',
    },
    {
      label: t('问题描述'),
      value: 'description',
    },
  ];

  return (
    <Form.Item>
      <Input.Group compact>
        <Select
          dropdownMatchSelectWidth={false}
          placeholder={t('todo.在以下内容中匹配')}
          value={selectedOption}
          onChange={(value) => {
            setSelectedOption(value);
            todoSearchFilterCache.set(value);
          }}
          options={searchOptions}
        />
        <Input.Search
          placeholder={t('todo.请输入匹配关键词')}
          onSearch={(value) => {
            const trimmedValue = value.trim();
            if (selectedOption === 'todoId' && trimmedValue && !/^\d+$/.test(trimmedValue)) {
              message.warn(t('待办编号只能输入数字'));
              return;
            }

            // 为了强制将上层filter的值覆盖，需要将所有选项的值都设置为undefined
            const optionsValue = Object.fromEntries(
              searchOptions.map((opt) => [
                opt.value,
                trimmedValue && selectedOption === opt.value ? trimmedValue : undefined,
              ]),
            ) as Record<string, string | undefined>;

            onFilterChange(optionsValue);
          }}
        />
      </Input.Group>
    </Form.Item>
  );
}

export function StatusFilter({
  todoStatusCountMap,
  filter: { todoStatus },
  onFilterChange,
}: TodoFilterProps<'todoStatus'> & {
  todoStatusCountMap?: Record<Todo.Status, number>;
}) {
  const { statusLabel, getStatusLabel, t } = useStatusLabel();

  return (
    <Radio.Group
      value={todoStatus}
      onChange={({ target: { value } }) => onFilterChange({ todoStatus: value as any })}
    >
      <Radio.Button value={-1}>
        {`${t('feedback.全部')} (${Object.values(todoStatusCountMap || {}).reduce(
          (res, cur) => res + cur,
          0,
        )})`}
      </Radio.Button>
      {orderBy(Object.keys(todoStatusCountMap || statusLabel), [
        (item) => {
          return { 1: 1, 4: 2, 2: 3, 3: 4 }[item];
        },
      ]).map((status) => {
        const label = getStatusLabel(status);

        const { [status]: count = 0 } = todoStatusCountMap || {};

        return (
          <Radio.Button
            key={status}
            value={parseInt(status as unknown as string, 10)}
          >{`${label} (${count})`}</Radio.Button>
        );
      })}
    </Radio.Group>
  );
}

export function TypeFilter({
  typeList: { typeOrder, otherType },
  typeCountMap,
  filter: { type },
  customTypeLabelMap,
  onFilterChange,
}: TodoFilterProps<'type'> & {
  typeList: Todo.TypeList;
  customTypeLabelMap: Record<string, string>;
  typeCountMap?: Record<Todo.Type, number>;
}) {
  const { getTypeLabel, t } = useTypeLabel();

  return (
    <div className="pl-[24px]">
      <Tabs
        activeKey={String(type)}
        onChange={(e) => {
          onFilterChange({ type: Number(e) });
        }}
        className={css`
          .ant-tabs-nav {
            margin-bottom: 0px;
          }
        `}
      >
        {typeOrder.map((cur) => {
          const label = officialTypeId.includes(cur) ? getTypeLabel(cur) : customTypeLabelMap[cur];
          const { [cur]: count = 0 } = typeCountMap || {};
          if (!label) return null;
          return (
            <Tabs.TabPane
              tab={
                <div className="flex">
                  <div className="max-w-[380px] truncate" title={String(label)}>
                    {label}
                  </div>
                  &nbsp;({count})
                </div>
              }
              key={String(cur)}
            />
          );
        })}
        {otherType.length > 0 && (
          <Tabs.TabPane tab={`${t('aide.其他')} (${typeCountMap?.[-1] || 0})`} key="-1" />
        )}
        <Tabs.TabPane tab={`${t('custom')} (${typeCountMap?.[-998] || 0})`} key="-998" />
      </Tabs>
    </div>
  );
}

const FilterForm = styled(Form)`
  > div {
    display: flex;
    align-items: center;
    width: 100%;
    flex-wrap: wrap;

    .ant-select {
      width: 160px;
      .ant-select-selector {
        width: 100%;
      }
    }
    .ant-input-search {
      width: 268px;
    }
  }
`;
